import * as React from 'react';
import { Typography, InputAdornment, Tooltip, CircularProgress, IconButton } from '@mui/material';

import Select from 'components/Select';
import { TextInput } from 'components/Input';
import { formatDollar, formatPercent } from 'helpers/formatters';
import { negativeNumberMask, numberMask } from 'helpers/masks';
import { Location } from 'types/location.type';
import GLPropertyTableRowModel from './GLPropertyTableRowModel';
import { LocationOptionsData } from 'types/types.type';
import TableChartIcon from '@mui/icons-material/TableChart';
import { YES_NO_OPTIONS } from '../../../../constants';
import Grid from '@mui/material/Unstable_Grid2';
import GLScoreInfo from '../GLScoreInfo';
import { calculateTotalGLPremium } from 'helpers/glCalculationHelper';
import { calculateAnnualizedPremiumFromTotalPremium } from '../../../../helpers/calculations';

export interface PropertyTableBodyFieldProps {
    location: Location;
    locationOptions: LocationOptionsData;
    matchingRowData: GLPropertyTableRowModel;
    classes: any;
    readonly: boolean;
    isLocationRenewal: boolean;
    isLocationProrataEndorsement: boolean;
    handleChange: (value: string, id: string) => void;
    handleShowHTMLTableClick: (rowData: GLPropertyTableRowModel) => void;
    effectiveDate?: string;
    expirationDate?: string;
}

export const getGLPropertyTableBodyFields = (props: PropertyTableBodyFieldProps) => {
    const {
        classes,
        handleChange,
        isLocationProrataEndorsement,
        isLocationRenewal,
        location,
        locationOptions,
        matchingRowData,
        readonly,
        handleShowHTMLTableClick,
        effectiveDate,
        expirationDate,
    } = props;
    const locationClassType = location.locationClassType;
    return [
        {
            value: location.locationName + ', ' + location.address1 + ', ' + location.city + ', ' + location.state,
            style: { textAlign: 'left', color: '#000000', fontWeight: 600 },
        },
        {
            value: (
                <Typography style={{ marginBottom: 0 }}>
                    {locationOptions.LOCATION_CLASSES[locationClassType]}
                </Typography>
            ),
        },
        {
            value: <Typography style={{ marginBottom: 0 }}>{matchingRowData.genAggLimit}</Typography>,
        },
        {
            value: <Typography style={{ marginBottom: 0 }}>{matchingRowData.premOpsOccurrenceLimit}</Typography>,
        },
        {
            value: <Typography style={{ marginBottom: 0 }}>{matchingRowData.HNOLimit}</Typography>,
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.premOpsDeductible}</Typography>
            ) : (
                <Select
                    classes={{ select: classes.select }}
                    inputId={`${location.id}:premOpsDeductible`}
                    optionsList={Object.values(locationOptions.LOCATION_GL_DED_OPTIONS)}
                    value={matchingRowData.premOpsDeductible}
                    onChange={(event: any) => handleChange(event.target.value, `${location.id}:premOpsDeductible`)}
                />
            ),
        },
        {
            value: (
                <Typography style={{ marginBottom: 0 }}>
                    {matchingRowData.expiringBaseRatedPremium
                        ? formatDollar(matchingRowData.expiringBaseRatedPremium, 3)
                        : 'N/A'}
                </Typography>
            ),
            isHidden: readonly || !isLocationRenewal,
        },
        {
            value: (
                <Typography style={{ marginBottom: 0 }}>
                    {matchingRowData.expiringIrpm || matchingRowData.expiringIrpm === 0
                        ? matchingRowData.expiringIrpm
                        : 'N/A'}
                </Typography>
            ),
            isHidden: readonly || !isLocationRenewal,
        },
        {
            value:
                matchingRowData?.baseRatedPremium && matchingRowData.baseRatedPremium < 0 ? (
                    <CircularProgress />
                ) : (
                    <>
                        <Typography style={{ marginBottom: 0 }}>
                            <Tooltip title="This value has been Rounded to nearest number">
                                <span>
                                    {!matchingRowData.baseRatedPremium
                                        ? '--'
                                        : formatDollar(matchingRowData.baseRatedPremium || 0, 0)}
                                </span>
                            </Tooltip>
                            {!!matchingRowData.baseRatedPremium && (
                                <IconButton size="small" onClick={() => handleShowHTMLTableClick(matchingRowData)}>
                                    <TableChartIcon />
                                </IconButton>
                            )}
                        </Typography>
                    </>
                ),
        },
        {
            value: (
                <Typography style={{ marginBottom: 0 }}>
                    {!matchingRowData.numberUnits ? '--' : matchingRowData.numberUnits}
                </Typography>
            ),
        },
        {
            value: (
                <Typography style={{ marginBottom: 0 }}>
                    {!matchingRowData.numberPools ? '--' : matchingRowData.numberPools}
                </Typography>
            ),
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.ratingScore}</Typography>
            ) : (
                <Grid container style={{ width: 100 }}>
                    <Grid>
                        <Select
                            classes={{ select: classes.select }}
                            inputId={`${location.id}:ratingScore`}
                            optionsList={Object.keys(locationOptions.LOCATION_GL_SCORE_OPTIONS)}
                            value={matchingRowData.ratingScore}
                            onChange={(event: any) => {
                                handleChange(event.target.value, `${location.id}:ratingScore`);
                            }}
                        />
                    </Grid>
                    <Grid>
                        <GLScoreInfo location={location} />
                    </Grid>
                </Grid>
            ),
        },
        {
            style: { minWidth: '100px' },
            value:
                (matchingRowData?.scoreIrpm || 0) < 0 ? (
                    <Typography style={{ marginBottom: 0 }}>{'N/A'}</Typography>
                ) : (
                    <Typography style={{ marginBottom: 0 }}>
                        {matchingRowData.scoreIrpm || matchingRowData.scoreIrpm === 0
                            ? matchingRowData.scoreIrpm
                            : (readonly && '') || ''}
                    </Typography>
                ),
        },
        {
            style: { minWidth: '100px' },
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>
                    {matchingRowData.irpm || matchingRowData.irpm === 0 ? matchingRowData.irpm : (readonly && '') || ''}
                </Typography>
            ) : (matchingRowData?.scoreIrpm || 0) < 0 ? (
                <Typography style={{ marginBottom: 0 }}>{'N/A'}</Typography>
            ) : (
                <TextInput
                    id={'irpm'}
                    inputStyle={{ textAlign: 'right' }}
                    placeholder="0.0"
                    value={
                        matchingRowData.irpm || matchingRowData.irpm === 0
                            ? matchingRowData.irpm
                            : (readonly && '') || ''
                    }
                    variant="underlined"
                    onBlur={(event: any) => {
                        handleChange(event.target.value, `${location.id}:irpm`);
                    }}
                    mask={negativeNumberMask}
                />
            ),
        },
        {
            value:
                (matchingRowData?.scoreIrpm || 0) < 0 ? (
                    <Typography style={{ marginBottom: 0 }}>{'N/A'}</Typography>
                ) : (
                    <Typography style={{ marginBottom: 0 }}>
                        {!matchingRowData.blendedDoorRate
                            ? '--'
                            : formatDollar(matchingRowData.blendedDoorRate || 0, 2)}
                    </Typography>
                ),
        },
        {
            style: { minWidth: '100px' },
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>{matchingRowData.triaPercentage + '%'}</Typography>
            ) : (
                <TextInput
                    inputStyle={{ textAlign: 'right' }}
                    placeholder="2.0"
                    value={matchingRowData.triaPercentage}
                    variant="underlined"
                    onBlur={(event: any) => handleChange(event.target.value, `${location.id}:triaPercentage`)}
                    endAdornment={<InputAdornment position="end">%</InputAdornment>}
                    mask={numberMask}
                />
            ),
        },
        {
            value:
                (matchingRowData?.scoreIrpm || 0) < 0 ? (
                    <Typography style={{ marginBottom: 0 }}>{'N/A'}</Typography>
                ) : matchingRowData?.totalPremium && matchingRowData.totalPremium < 0 ? (
                    <CircularProgress />
                ) : (
                    <Tooltip title="This value has been Rounded to nearest number">
                        <Typography style={{ marginBottom: 0 }}>
                            {/*
                            If this is a location prorata endorsement, we need to use the ANNUALIZED premium.
                            If this is not a location prorata endorsement, we need to use the TOTAL premium.

                            If readonly, certain derived fields won't be populated and we need to recalculate.
                            */}
                            {isLocationProrataEndorsement
                                ? readonly
                                    ? // Effective date and expiration date are for this current endorsement.
                                      // Use them along with this endorsement's total premium to calculate annualized premium.
                                      formatDollar(
                                          calculateAnnualizedPremiumFromTotalPremium(
                                              matchingRowData.totalPremium || 0,
                                              effectiveDate,
                                              expirationDate
                                          ),
                                          0
                                      )
                                    : formatDollar(matchingRowData.annualizedPremium || 0, 0)
                                : readonly
                                ? // If readonly, we won't have totalPremium since it's a derived field. Calculate it.
                                  formatDollar(
                                      calculateTotalGLPremium(
                                          matchingRowData.irpm,
                                          matchingRowData.baseRatedPremium,
                                          matchingRowData.triaPercentage
                                      )
                                  )
                                : // If not readonly, we will only have totalPremium once the needed input values are given by the user. Until then show a dash.
                                matchingRowData.totalPremium
                                ? formatDollar(matchingRowData.totalPremium, 0)
                                : '--'}
                        </Typography>
                    </Tooltip>
                ),
        },
        {
            value: (
                // Total Premium / Endorsement Premium column
                <Tooltip title="This value has been Rounded to nearest number">
                    <Typography style={{ marginBottom: 0 }}>
                        {formatDollar(
                            readonly ? matchingRowData.totalPremium || 0 : matchingRowData.endorsementPremium || 0
                        )}
                    </Typography>
                </Tooltip>
            ),
            isHidden: !isLocationProrataEndorsement,
        },
        {
            value: readonly ? (
                <Typography style={{ marginBottom: 0 }}>
                    {(matchingRowData.inspectionsRequiredCount || 0) > 0 ? YES_NO_OPTIONS.YES : YES_NO_OPTIONS.NO}
                </Typography>
            ) : (
                <Select
                    classes={{ select: classes.select }}
                    inputId={`${location.id}:inspectionsRequiredCount`}
                    options={YES_NO_OPTIONS}
                    defaultValue={YES_NO_OPTIONS.YES}
                    value={(matchingRowData.inspectionsRequiredCount || 0) > 0 ? YES_NO_OPTIONS.YES : YES_NO_OPTIONS.NO}
                    onChange={(event: any) =>
                        handleChange(
                            event.target.value.toString().toUpperCase() === YES_NO_OPTIONS.YES.toUpperCase()
                                ? '1'
                                : '0',
                            `${location.id}:inspectionsRequiredCount`
                        )
                    }
                />
            ),
        },
    ].filter((r) => !r.isHidden);
};
